<template>
  <div
    v-if="shouldRender"
    class="tw-flex tw-flex-col tw-gap-2"
    data-testid="highlighted-metrics-section"
  >
    <h3 class="tw-text-sm tw-font-medium tw-text-sf-neutral-dark-1 tw-m-0">
      {{ I18n.t('report.drawer.highlighted.section_title') }}
    </h3>

    <div
      v-if="fullscreen"
      class="tw-grid tw-grid-cols-2 tw-gap-2"
      data-testid="highlighted-metrics-fullscreen-grid"
    >
      <HighlightedMetricPill
        v-for="entry in store.highlighted"
        :key="`fullscreen-${entry.id}`"
        :entry="entry"
        :expanded="true"
        :hide-collapse="true"
        :read-only="readOnly"
      />
    </div>

    <template v-else>
      <HighlightedMetricPill
        v-for="entry in expanded"
        :key="`expanded-${entry.id}`"
        :entry="entry"
        :expanded="true"
        :read-only="readOnly"
        @update:expanded="(value) => onToggle(entry.id, value)"
      />

      <Draggable
        v-model="rowEntries"
        class="tw-grid tw-grid-cols-3 tw-gap-2"
        item-key="id"
        handle=".handle"
        :animation="200"
        :disabled="readOnly || store.setupLocked"
        @start="dragging = true"
        @end="onDragEnd"
      >
        <template #item="{ element: entry }">
          <HighlightedMetricPill
            :entry="entry"
            :expanded="false"
            :read-only="readOnly"
            :dragging="dragging"
            @update:expanded="(value) => onToggle(entry.id, value)"
          />
        </template>
        <template #footer>
          <div
            v-for="i in emptySlotsCount"
            :key="`empty-${i}`"
            class="
              tw-rounded-lg
              tw-border
              tw-border-dashed
              tw-border-sf-slate-lighter-2
              tw-px-3
              tw-py-4
              tw-flex
              tw-items-center
              tw-justify-center
              tw-text-center
              tw-text-xs
              tw-text-sf-neutral-light-1
            "
            data-testid="highlighted-metrics-empty-slot"
          >
            {{ I18n.t('report.drawer.highlighted.empty_slot') }}
          </div>
        </template>
      </Draggable>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import Draggable from 'vuedraggable'

import HighlightedMetricPill from '@app/application/report/components/graphs-drawer/highlighted-metric-pill.vue'
import { useExpandedSet } from '@app/application/report/composables/use-expanded-set'
import {
  HIGHLIGHTED_LIMIT,
  useReportGraphsDrawer,
} from '@app/application/report/store/use-report-graphs-drawer'
import type { DrawerGraphEntry } from '@app/application/report/types'
import useI18n from '@app/shared/vue/composables/use-I18n'

defineOptions({
  name: 'HighlightedMetricsSection',
})

interface Props {
  readOnly?: boolean
  fullscreen?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  readOnly: false,
  fullscreen: false,
})

const I18n = useI18n()
const store = useReportGraphsDrawer()

const slotCount = HIGHLIGHTED_LIMIT
const dragging = ref(false)

const { expanded, row, onToggle } = useExpandedSet(() => store.highlighted)

// Mirrors the composable's `row` into a writable ref because vuedraggable
// mutates its v-model during a drag. Resyncs whenever `row` changes
// (collapse, server patch, rollback).
const rowEntries = ref<DrawerGraphEntry[]>([])
watch(
  row,
  (next) => {
    rowEntries.value = [...next]
  },
  { immediate: true }
)

const emptySlotsCount = computed(() =>
  props.readOnly || store.setupLocked
    ? 0
    : Math.max(0, slotCount - rowEntries.value.length)
)

const shouldRender = computed(
  () => store.highlightedCount > 0 || (!props.readOnly && !store.setupLocked)
)

function onDragEnd(): void {
  dragging.value = false

  // Build the full ordered id list: expanded entries keep their original
  // position; slim entries are taken from the row in their dragged order.
  const expandedIds = new Set(expanded.value.map((entry) => entry.id))
  const newSlimIds = rowEntries.value.map((entry) => entry.id)
  const fullOrder: number[] = []
  let slimIdx = 0
  for (const entry of store.highlighted) {
    if (expandedIds.has(entry.id)) {
      fullOrder.push(entry.id)
    } else {
      fullOrder.push(newSlimIds[slimIdx] ?? entry.id)
      slimIdx += 1
    }
  }

  const currentIds = store.highlighted.map((entry) => entry.id)
  const unchanged = fullOrder.every((id, i) => id === currentIds[i])
  if (unchanged) return

  void store.reorderHighlighted(fullOrder)
}
</script>
